import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "const/constants";
import { useQuery } from "@tanstack/react-query";
import useKothar from "context/useKothar";
import { useCallback, useMemo, useState } from "react";
import { patchDocuments } from "./admissionApi";
import EmailDocumentsDialog from "./EmailDocumentsDialog";
import EntryDocumentsPanel from "components/ModuleDocuments/EntryDocumentsPanel";
import { serializeAdmissionAttachments } from "./normalizeAdmissionAttachments";
import { fetchLinkedDocuments } from "views/admin/Clients/documents/clientDocuments.api";
import { useAdmissionSignedDocuments } from "./useAdmissionSignedDocuments";
import { useDropzone } from "react-dropzone";
import { FaCloudUploadAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { MAX_UPLOAD_FILE_SIZE_BYTES } from "constants/fileUploadLimits";
import { getFileIcon, getOriginalFileName } from "utils/utils";
import { buildFlowDocument } from "utils/documentMetaUtils";

const MAX_FILE_SIZE = MAX_UPLOAD_FILE_SIZE_BYTES;

const saveErrorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AdmissionAttachmentsTab = ({
  attachments = [],
  setAttachments,
  admissionId,
  universityId,
  universityName,
  universityEmail,
  clientName,
  clientStudentId,
  clientEmail,
  clientId,
  clientPhone,
  onSaveAndContinue,
  onSaveAndExit,
}) => {
  const [{ token }] = useKothar();
  const [modalOpen, setModalOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [pendingUrl, setPendingUrl] = useState(null);
  const [pendingFileLabel, setPendingFileLabel] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { signedDocuments, isLoading: isLoadingSignedDocuments } =
    useAdmissionSignedDocuments(admissionId);
  const displayRows = useMemo(
    () => [...attachments, ...signedDocuments],
    [attachments, signedDocuments]
  );

  // A document shown on this admission only via "Show in module" never lands in `attachments` --
  // it stays a linked ClientDocument. Without this, Email documents stays disabled even though
  // EmailDocumentsDialog has something to offer.
  const { data: linkedDocuments = [] } = useQuery(
    ["email-documents-linked-admission", admissionId],
    () => fetchLinkedDocuments("ADMISSION", admissionId),
    { enabled: Boolean(admissionId) }
  );
  const canEmailDocuments = attachments.length > 0 || linkedDocuments.length > 0;

  // The whole list goes up, not just the removal: an admission saved before attachments existed
  // shows documents gathered from older fields, and this save is what first files them here.
  const removeAttachment = async (doc) => {
    const next = attachments.filter((item) => item.id !== doc.id);
    if (admissionId) {
      try {
        await patchDocuments(admissionId, serializeAdmissionAttachments(next));
      } catch (err) {
        throw new Error(saveErrorMessage(err, "Could not remove the document. Try again."));
      }
    }
    setAttachments(next);
  };

  const resetDraft = () => {
    setDocumentName("");
    setPendingUrl(null);
    setPendingFileLabel("");
  };

  const openModal = () => {
    resetDraft();
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetDraft();
  };

  const uploadFile = useCallback(
    async (file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          `File is too large (${formatFileSize(file.size)}). Maximum is ${formatFileSize(
            MAX_FILE_SIZE
          )}.`
        );
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      setIsUploading(true);
      try {
        const res = await axios.post(`${API_URL}/file/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
        setPendingUrl(res?.data?.url);
        setPendingFileLabel(file.name);
        toast.success("File uploaded");
      } catch (err) {
        const status = Number(err?.response?.status ?? err?.request?.status);
        if (status === 413) {
          toast.error("File is too large. Please use a smaller file.");
        } else {
          toast.error(err?.response?.data?.errorMessage || "Error uploading file");
        }
      } finally {
        setIsUploading(false);
      }
    },
    [token]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => files[0] && uploadFile(files[0]),
    onDropRejected: (rejections) => {
      const tooLarge = rejections.some((r) => r.errors?.some((e) => e.code === "file-too-large"));
      if (tooLarge) toast.error(`Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}.`);
    },
    multiple: false,
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    disabled: isUploading,
  });

  const handleAddDocument = async () => {
    const name = documentName.trim();
    if (!name) {
      toast.error("Enter a document name (e.g. Passport, Offer letter).");
      return;
    }
    if (!pendingUrl) {
      toast.error("Upload a file.");
      return;
    }

    const newDoc = buildFlowDocument({ name, url: pendingUrl });
    const next = [...attachments, newDoc];

    if (admissionId) {
      setIsSaving(true);
      try {
        await patchDocuments(admissionId, serializeAdmissionAttachments(next));
        setAttachments(next);
        closeModal();
        toast.success("Document added and saved.");
      } catch (err) {
        toast.error(saveErrorMessage(err, "Failed to save document. Try again."));
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // No admission record yet (new draft) — keep locally; persisted when the admission is created.
    setAttachments(next);
    closeModal();
    toast.success("Document added.");
  };

  const canSave = Boolean(documentName.trim() && pendingUrl && !isUploading);

  return (
    <div className="space-y-6">
      <EntryDocumentsPanel
        moduleType="ADMISSION"
        entryId={admissionId}
        clientId={clientId}
        documents={displayRows}
        onAddClick={openModal}
        onRemove={removeAttachment}
        loading={isLoadingSignedDocuments}
        extraActions={
          <Tooltip
            title={
              !admissionId
                ? "Save the admission first"
                : !canEmailDocuments
                ? "Add documents first"
                : "Email selected documents"
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MailOutlineIcon />}
                onClick={() => setEmailOpen(true)}
                disabled={!canEmailDocuments || !admissionId}
              >
                Email documents
              </Button>
            </span>
          </Tooltip>
        }
      />
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle className="font-semibold text-app-text">Add document</DialogTitle>
        <DialogContent className="space-y-4 pt-1">
          <TextField
            label="Document name"
            placeholder="e.g. Passport, Academic transcript, Offer letter"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            fullWidth
            size="small"
            required
            autoFocus
          />

          <div>
            <Typography variant="subtitle2" className="text-app-text mb-2">
              File
            </Typography>
            <div
              {...getRootProps()}
              className={`flex flex-col items-center justify-center w-full min-h-[120px] border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isDragActive
                  ? "border-orange-400 bg-orange-50"
                  : "border-app-border bg-app-surface-muted hover:bg-app-surface-muted"
              } ${isUploading ? "pointer-events-none opacity-70" : ""}`}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <CircularProgress size={32} />
              ) : (
                <>
                  <FaCloudUploadAlt className="w-9 h-9 mb-2 text-app-text-muted" />
                  <Typography variant="body2" className="text-app-text-secondary font-medium">
                    Click or drag a file to upload
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF, Word, images — max {formatFileSize(MAX_FILE_SIZE)}
                  </Typography>
                </>
              )}
            </div>

            {pendingUrl && (
              <Paper
                variant="outlined"
                className="mt-3 p-3 border-emerald-200 bg-emerald-50/50 flex items-center gap-3"
              >
                <span className="text-2xl shrink-0">{getFileIcon(pendingUrl)}</span>
                <div className="min-w-0 flex-1">
                  <Typography variant="body2" className="font-medium truncate">
                    {pendingFileLabel || getOriginalFileName(pendingUrl)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    File ready
                  </Typography>
                </div>
                <Button size="small" color="inherit" onClick={() => setPendingUrl(null)}>
                  Remove
                </Button>
              </Paper>
            )}
          </div>
        </DialogContent>
        <DialogActions className="px-4 pb-3">
          <Button onClick={closeModal} color="inherit" disabled={isUploading || isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddDocument}
            disabled={!canSave || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? "Saving…" : "Add document"}
          </Button>
        </DialogActions>
      </Dialog>

      <EmailDocumentsDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        studentId={admissionId}
        documents={attachments}
        universityId={universityId}
        universityName={universityName}
        universityEmail={universityEmail}
        clientName={clientName}
        clientStudentId={clientStudentId}
        clientEmail={clientEmail}
        clientId={clientId}
        clientPhone={clientPhone}
      />
      {/* Documents save as they are added or removed, so these only move on. */}
      {(onSaveAndExit || onSaveAndContinue) && (
        <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-app-border pt-4">
          {onSaveAndExit && (
            <Button variant="outlined" color="inherit" onClick={onSaveAndExit}>
              Exit
            </Button>
          )}
          {onSaveAndContinue && (
            <Button variant="contained" color="primary" onClick={onSaveAndContinue}>
              Continue
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdmissionAttachmentsTab;
